import { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { Session, User as SupabaseUser } from '@supabase/supabase-js';
import { supabase, isSupabaseConfigured } from '../services/supabase';
import type { Subscriber } from '../types';
import { trackEvent, setAnalyticsUserId } from '../utils/analytics';
import { flushSignupIntent } from '../utils/signupIntent';
import { clearUserScopedCaches } from '../utils/userCache';

const SUBSCRIBER_CACHE_KEY = 'bt-vision-subscriber-cache';

interface AuthContextType {
  session: Session | null;
  user: SupabaseUser | null;
  subscriber: Subscriber | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  needsLetzplayConnection: boolean;
  signInWithMagicLink: (email: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshSubscriber: () => Promise<void>;
  setSubscriber: (subscriber: Subscriber) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [subscriber, setSubscriber] = useState<Subscriber | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Refs for request deduplication - concurrent callers await the same in-flight
  // request instead of resolving early with subscriber still null (that early
  // resolve let isLoading flip to false before the subscriber was known, which
  // bounced deep links through /connect → /dashboard on reload)
  const fetchPromiseRef = useRef<Promise<void> | null>(null);
  const lastFetchTimeRef = useRef<number>(0);
  // Mirrors `subscriber` for the auth listener, whose closure would otherwise
  // read a stale value and treat every token refresh as a first sign-in.
  const subscriberRef = useRef<Subscriber | null>(null);
  subscriberRef.current = subscriber;

  const isAuthenticated = !!session && !!user;
  const needsLetzplayConnection = isAuthenticated && !subscriber?.letzplayUsername;

  // Helper to clear all auth state (used when account is deleted)
  const clearAuthState = useCallback(() => {
    setSession(null);
    setUser(null);
    setSubscriber(null);
    localStorage.removeItem('sb-access-token');
    localStorage.removeItem(SUBSCRIBER_CACHE_KEY);
    // Rankings, standings and stats are cached for instant paint. Leaving them
    // behind means the next person to sign in on this device sees them.
    clearUserScopedCaches();
    setAnalyticsUserId(null);
  }, []);

  // Fetch subscriber data from our backend with deduplication and caching
  const fetchSubscriber = useCallback(async (accessToken: string) => {
    // Join the in-flight request (deduplication) so callers only resolve once
    // the subscriber data has actually arrived
    if (fetchPromiseRef.current) {
      console.log('[Auth] Joining in-flight /api/auth/me request');
      return fetchPromiseRef.current;
    }

    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchTimeRef.current;

    // Skip if we just fetched recently (cache for 5 seconds)
    if (timeSinceLastFetch < 5000) {
      console.log(`[Auth] Skipping /api/auth/me request - fetched ${timeSinceLastFetch}ms ago (cache)`);
      return;
    }

    console.log(`[Auth] Fetching /api/auth/me, last fetch was ${timeSinceLastFetch}ms ago`);

    lastFetchTimeRef.current = now;

    const fetchPromise = (async () => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/api/auth/me`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          console.log('[Auth] Successfully fetched subscriber data');
          // Signing in as someone else without signing out first (a magic link
          // for another account, say) never runs clearAuthState, so catch the
          // identity change here too — before any page can paint the previous
          // account's cached rankings.
          const previousId = subscriberRef.current?.id;
          if (previousId && previousId !== data.subscriber.id) {
            console.warn('[Auth] Different account signed in, clearing cached data');
            clearUserScopedCaches();
          }
          setSubscriber(data.subscriber);
          // Identify here rather than only at sign-in: a returning visit never
          // fires SIGNED_IN, it just bootstraps a stored session through this
          // path, and PostHog's own storage can be cleared independently of
          // Supabase's. Repeating it is free when nothing changed.
          if (data.subscriber.userId) {
            setAnalyticsUserId(data.subscriber.userId, {
              email: data.subscriber.email,
              name: data.subscriber.name ?? undefined,
              letzplay_connected: Boolean(data.subscriber.letzplayUsername),
              signed_up_at: data.subscriber.createdAt,
            });
          }
          // Attach any landing-page CTA click to the account now that it exists.
          void flushSignupIntent();
          // Cache subscriber for offline use
          try {
            localStorage.setItem(SUBSCRIBER_CACHE_KEY, JSON.stringify(data.subscriber));
          } catch { /* ignore storage errors */ }
        } else if (response.status === 404 || response.status === 401) {
          // 404: the account was deleted. 401: we hold a session the backend
          // will not accept — expired past refresh, or issued by a different
          // Supabase project than the one the API validates against.
          //
          // Either way the session is unusable, and leaving it in place is the
          // worst outcome: isAuthenticated stays true off the client-side
          // session alone, so ProtectedRoute waves the user through and every
          // page renders its empty state ("Ranking não encontrado") while all
          // requests 401 behind it. Clear it and let ProtectedRoute bounce to
          // /login, which keeps the deep link in ?redirect=.
          const data = await response.json().catch(() => ({}));
          console.warn(
            `[Auth] Session rejected by the API (${response.status}), signing out:`,
            data.error?.code
          );
          clearAuthState();
          // No need to await — state is already cleared.
          supabase.auth.signOut();
        } else {
          console.warn('[Auth] Failed to fetch subscriber:', response.status, response.statusText);
        }
      } catch (error) {
        console.error('[Auth] Failed to fetch subscriber:', error);
        // Offline fallback: use cached subscriber data
        if (!navigator.onLine) {
          try {
            const cached = localStorage.getItem(SUBSCRIBER_CACHE_KEY);
            if (cached) {
              console.log('[Auth] Offline — using cached subscriber data');
              setSubscriber(JSON.parse(cached));
            }
          } catch { /* ignore parse errors */ }
        }
      } finally {
        fetchPromiseRef.current = null;
      }
    })();

    fetchPromiseRef.current = fetchPromise;
    return fetchPromise;
  }, [clearAuthState]);

  const refreshSubscriber = useCallback(async () => {
    if (session?.access_token) {
      console.log('[Auth] Manual refreshSubscriber called');
      // Force refresh by resetting last fetch time to bypass 5-second cache
      lastFetchTimeRef.current = 0;
      await fetchSubscriber(session.access_token);
    } else {
      console.log('[Auth] refreshSubscriber called but no access token available');
    }
  }, [session?.access_token, fetchSubscriber]);

  useEffect(() => {
    console.log('[Auth] AuthProvider useEffect running');

    // Check if mock auth is enabled (dev mode)
    const isMockAuthEnabled = import.meta.env.VITE_MOCK_AUTH === 'true';

    if (isMockAuthEnabled) {
      console.log('[Auth] Mock auth enabled - auto-logging in with test user');

      // Mock session data
      const mockSession = {
        access_token: 'mock-access-token',
        refresh_token: 'mock-refresh-token',
        expires_in: 3600,
        token_type: 'bearer',
        user: {
          id: '25981aba-badd-456a-b470-c718bb1f6de6',
          email: 'yagorp15+test01@gmail.com',
          aud: 'authenticated',
          role: 'authenticated',
          app_metadata: {},
          user_metadata: {},
          created_at: new Date().toISOString(),
        }
      } as Session;

      // Mock subscriber data
      const mockLetzplayConnected = import.meta.env.VITE_MOCK_LETZPLAY_CONNECTED !== 'false';
      // Models a fresh magic-link signup: authenticated, but no profile yet.
      const mockNewUser = import.meta.env.VITE_MOCK_NEW_USER === 'true';
      const mockSubscriber: Subscriber = {
        id: '58c988a7-0709-4d24-8e76-317236d80230',
        email: 'yagorp15+test01@gmail.com',
        name: mockNewUser ? null : 'Yago Pessoa',
        avatarUrl: mockNewUser
          ? null
          : 'https://res.cloudinary.com/lptennis/image/upload/c_fit,h_250,q_auto:low,w_250/v1769459517/gzmlchojq0gjihpkcccm.webp?_a=BACAGSEv',
        userId: null,
        letzplayUsername: mockNewUser || !mockLetzplayConnected ? null : 'yagopessoa',
        cpf: null,
        birthDate: null,
        lastSyncAt: '2026-02-03T20:25:39.154+00:00',
        createdAt: '2026-02-03T20:21:57.050419+00:00',
        pendingRankingMatches: [],
        wildcardNeededRankings: [],
      };

      // Set mock auth state
      setSession(mockSession);
      setUser(mockSession.user);
      setSubscriber(mockSubscriber);
      setIsLoading(false);

      // Store mock token for API calls
      localStorage.setItem('sb-access-token', mockSession.access_token);
      setAnalyticsUserId(mockSession.user.id, {
        email: mockSubscriber.email,
        name: mockSubscriber.name ?? undefined,
        letzplay_connected: Boolean(mockSubscriber.letzplayUsername),
        signed_up_at: mockSubscriber.createdAt,
      });

      console.log('[Auth] Mock auth setup complete');
      return; // Skip real Supabase auth
    }

    // Skip if Supabase is not configured (dev mode)
    if (!isSupabaseConfigured) {
      console.log('[Auth] Supabase not configured, skipping auth setup');
      setIsLoading(false);
      return;
    }

    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      console.log('[Auth] Initial session fetched:', session ? 'has session' : 'no session');
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.access_token) {
        await fetchSubscriber(session.access_token);
      }

      setIsLoading(false);
    });

    // Skip auth listener if not configured
    if (!isSupabaseConfigured) {
      return;
    }

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      console.log(`[Auth] onAuthStateChange event: ${_event}, has session: ${!!session}`);
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.access_token) {
        // Store token for API calls
        localStorage.setItem('sb-access-token', session.access_token);

        // Track successful login
        if (_event === 'SIGNED_IN') {
          const provider = session.user.app_metadata?.provider || 'email';
          trackEvent('btv_auth_login_success', {
            userId: session.user.id,
            email: session.user.email,
            provider,
          });
          trackEvent(provider === 'google' ? 'btv_auth_google_login_used' : 'btv_auth_magic_link_used', {
            userId: session.user.id,
            email: session.user.email
          });
          // Set user ID for analytics. The email is all we have until
          // /api/auth/me answers — fetchSubscriber fills in the rest.
          setAnalyticsUserId(session.user.id, { email: session.user.email });
        }

        // setSession above already flipped isAuthenticated, so without this the
        // guards would wave the user through with subscriber still null and
        // paint a signed-in-but-empty hub for the length of this fetch — then
        // yank it away once the profile lands. Only when we have no subscriber
        // yet: a routine token refresh must not blank the app mid-use.
        const isFirstLoad = subscriberRef.current === null;
        if (isFirstLoad) setIsLoading(true);
        await fetchSubscriber(session.access_token);
        if (isFirstLoad) setIsLoading(false);
      } else {
        console.log('[Auth] No session, clearing auth data');
        localStorage.removeItem('sb-access-token');
        setSubscriber(null);
      }
    });

    return () => {
      console.log('[Auth] Cleaning up auth subscription');
      subscription.unsubscribe();
    };
  }, [fetchSubscriber]);

  const signInWithMagicLink = useCallback(async (email: string) => {
    console.log('[Auth] signInWithMagicLink called for email:', email);

    // Track login attempt
    trackEvent('btv_auth_login_attempt', { email });

    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) {
      // Track login failure
      trackEvent('btv_auth_login_failed', { email, error: error.message });
    } else {
      // Track magic link sent
      trackEvent('btv_auth_magic_link_sent', { email });
    }

    return { error };
  }, []);

  const signInWithGoogle = useCallback(async () => {
    trackEvent('btv_auth_google_login_attempt');

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) {
      trackEvent('btv_auth_google_login_failed', { error: error.message });
      throw error;
    }
  }, []);

  const signOut = useCallback(async () => {
    console.log('[Auth] signOut called');

    // Track logout
    trackEvent('btv_auth_logout');

    const isMockAuthEnabled = import.meta.env.VITE_MOCK_AUTH === 'true';

    if (!isMockAuthEnabled) {
      // Use scope: 'local' to force-clear the local session even if the server
      // request fails (e.g., when token is already expired/invalid)
      await supabase.auth.signOut({ scope: 'local' });
    }

    // Clear React state
    setSession(null);
    setUser(null);
    setSubscriber(null);

    // Clear our custom token and cached subscriber
    localStorage.removeItem('sb-access-token');
    localStorage.removeItem(SUBSCRIBER_CACHE_KEY);

    if (!isMockAuthEnabled) {
      // Also clear Supabase's internal storage (keys start with 'sb-')
      const keysToRemove = Object.keys(localStorage).filter(key => key.startsWith('sb-'));
      keysToRemove.forEach(key => localStorage.removeItem(key));
    }

    // Clear user ID from analytics
    setAnalyticsUserId(null);
  }, []);

  const value: AuthContextType = {
    session,
    user,
    subscriber,
    isLoading,
    isAuthenticated,
    needsLetzplayConnection,
    signInWithMagicLink,
    signInWithGoogle,
    signOut,
    refreshSubscriber,
    setSubscriber,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
